/**
 * Team Health Widget (issue #1657, part of epic #1650)
 *
 * Scorecard for a single team's service portfolio health, matching
 * `homepage-dashboard-spec.md`'s "Team Health Dashboard" widget. `teamId`
 * defaults to the caller's own team server-side when omitted — see
 * `backend/src/graphql/resolvers/teamHealth.ts`'s module doc for the
 * membership/primary-contact/Administrator authorization rule. Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import {
  useTeamHealth,
  type TeamHealthMetrics,
  type TeamHealthTeamInfo,
} from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './TeamHealthWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'team-health',
  title: 'Team Health',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface TeamHealthStats {
  team: TeamHealthTeamInfo | null;
  metrics: TeamHealthMetrics | null;
  recommendations: string[];
}

type TeamHealthQueryData = ReturnType<typeof useTeamHealth>['data'];

function extractTeamHealthStats(data: TeamHealthQueryData): TeamHealthStats | null {
  if (!data?.teamHealth) {
    return null;
  }
  return data.teamHealth;
}

export function TeamHealthWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useTeamHealth(),
    extractTeamHealthStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || !stats.team || !stats.metrics) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="team-health-widget__empty">
          <IconElement name="groups" className="team-health-widget__empty-icon" />
          <p className="forge-typography--body2">
            No team to show — join a team or select one to see its health
          </p>
        </div>
      </DashboardWidget>
    );
  }

  const { team, metrics, recommendations } = stats;

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="team-health-widget__content">
        <div className="team-health-widget__header">
          <span className="team-health-widget__team-name forge-typography--label1">
            {team.name}
          </span>
          <span className="team-health-widget__division forge-typography--caption">
            {team.division}
          </span>
        </div>

        <div className="team-health-widget__metric-grid" data-testid="team-health-metrics">
          <div className="team-health-widget__metric">
            <div className="team-health-widget__metric-value">{metrics.serviceCount}</div>
            <div className="team-health-widget__metric-label forge-typography--label2">
              Services
            </div>
          </div>
          <div className="team-health-widget__metric">
            <div className="team-health-widget__metric-value">{metrics.productionServices}</div>
            <div className="team-health-widget__metric-label forge-typography--label2">
              Production
            </div>
          </div>
          <div className="team-health-widget__metric">
            <div className="team-health-widget__metric-value">{metrics.avgComplexity}</div>
            <div className="team-health-widget__metric-label forge-typography--label2">
              Avg Complexity
            </div>
          </div>
          <div className="team-health-widget__metric">
            <div className="team-health-widget__metric-value">{metrics.deprecatedServices}</div>
            <div className="team-health-widget__metric-label forge-typography--label2">
              Deprecated
            </div>
          </div>
          <div className="team-health-widget__metric">
            <div className="team-health-widget__metric-value">{metrics.techDebtScore}</div>
            <div className="team-health-widget__metric-label forge-typography--label2">
              Tech Debt Score
            </div>
          </div>
          <div className="team-health-widget__metric">
            <div className="team-health-widget__metric-value">{metrics.offStrategyTechCount}</div>
            <div className="team-health-widget__metric-label forge-typography--label2">
              Off-Strategy Tech
            </div>
          </div>
        </div>

        {recommendations.length > 0 && (
          <ul
            className="team-health-widget__recommendations"
            data-testid="team-health-recommendations"
          >
            {recommendations.map((recommendation) => (
              <li key={recommendation} className="team-health-widget__recommendation">
                <IconElement
                  name="lightbulb"
                  className="team-health-widget__recommendation-icon"
                  aria-hidden="true"
                />
                <span className="forge-typography--body2">{recommendation}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardWidget>
  );
}
